import { SlickGrid } from "slickgrid"
import { startIcons } from "./import_lucid"

// The spreadsheet PharOS built, open beside the chat the way Excel shows it: row numbers, column
// letters, a formula bar and the sheet tabs along the bottom. It draws /chats/:id/spreadsheet.json,
// every cell of the file with each formula's value worked out (python/pharos_spreadsheet.py), since
// the file itself carries formulas with no results. SlickGrid draws only the rows in view, so a
// 10,000-row Data sheet opens as fast as a summary.
// A file the user uploaded opens the same way, from /chats/uploads/:id/preview (Chat::Uploads::Preview).
//
// A sheet with money opens at the file's scale; the select in the header rescales its amounts as
// the file's own "Amounts in ₹ …" dropdown does, from the rupee figure each cell carries ("m").
//
// One panel per page. The chat shell (chat_split_controller) hears pharos:sheet-open/-close and
// folds its sidebar away to make room; anywhere else the panel simply lies over the page.

const ROW_HEIGHT = 24
const CHAR_PX = 7.5
// A caption runs across the sheet, as it does in Excel; so does an uploaded file's "more rows" line.
const SPANS = new Set(['title', 'sub', 'scale', 'more'])

let panel = null
let grid = null
let sheets = []
let opened = 0 // the latest open; a slower earlier fetch must not draw over it
let scale = null // { divisor, opening, options, money: Intl.NumberFormat } while the file has money

const esc = (text) => String(text ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]))

// 0 → A, 25 → Z, 26 → AA
const letter = (i) => {
  let name = ''
  for (let n = i + 1; n > 0; n = Math.floor((n - 1) / 26)) name = String.fromCharCode(65 + (n - 1) % 26) + name
  return name
}

// What a cell shows: an amount at the chosen scale (at the file's own, the preview's text as is).
const cellText = (row, i) => {
  if (scale && row.k === 'scale') return scale.options.find((o) => o.value === scale.divisor)?.label ?? row.c[i] ?? ''
  const amount = row.m?.[i]
  if (amount == null || !scale || scale.divisor === scale.opening) return row.c[i] ?? ''
  return amount === 0 ? '-' : scale.money.format(amount / scale.divisor)
}

// The preview lists only rows that hold something; a sheet shows every row number up to its last.
const everyRow = (rows) => {
  const at = new Map(rows.map((row) => [row.r, row]))
  const last = rows.reduce((max, row) => Math.max(max, row.r), 0)
  return Array.from({ length: last }, (_, i) => at.get(i + 1) || { r: i + 1, k: 'blank', c: [] })
}

export function openSheet({ title, jsonUrl, downloadUrl, ext = 'xlsx' }) {
  if (!panel?.isConnected) panel = build()
  const seq = ++opened
  panel.querySelector('[data-xv-title]').textContent = title
  panel.querySelector('[data-xv-ext]').textContent = `· ${ext.toUpperCase()}`
  panel.querySelector('[data-xv-download]').href = downloadUrl
  panel.querySelector('[data-xv-tabs]').innerHTML = ''
  setBody('<div class="laz-xv-msg">Loading…</div>')
  setScale(null)
  panel.hidden = false
  window.dispatchEvent(new CustomEvent('pharos:sheet-open'))

  fetch(jsonUrl, { headers: { Accept: 'application/json' } })
    .then((response) => (response.ok ? response.json() : Promise.reject(response)))
    .then((data) => {
      if (seq !== opened) return
      sheets = data.sheets || []
      setScale(data.scale)
      panel.querySelector('[data-xv-tabs]').innerHTML = sheets
        .map((sheet, i) => `<button type="button" role="tab" data-xv-tab="${i}">${esc(sheet.name)}</button>`)
        .join('')
      show(0)
    })
    .catch(() => {
      if (seq === opened) setBody('<div class="laz-xv-msg">The preview could not be loaded. Download the file to open it in Excel.</div>')
    })
}

function build() {
  const el = document.createElement('aside')
  el.className = 'laz-xv'
  el.hidden = true
  el.setAttribute('aria-label', 'Spreadsheet')
  el.setAttribute('data-turbo-temporary', '') // not in the page Turbo keeps for Back: it would come back without its listeners
  el.innerHTML = `
    <div class="laz-xv-head">
      <div class="laz-xv-name"><span data-xv-title></span><span class="ext" data-xv-ext></span></div>
      <select data-xv-scale hidden aria-label="Scale" title="Scale of the amounts"
        class="mr-1 h-ctl pl-2.5 pr-8 py-0 rounded-ctl border border-hair-hi bg-surface text-ui text-ink focus:outline-none focus:border-brand focus:ring-4 focus:ring-brand-soft"></select>
      <a class="laz-xv-btn" data-xv-download data-turbo="false" title="Download" aria-label="Download"><i data-lucide="download"></i></a>
      <button type="button" class="laz-xv-btn" data-xv-expand title="Expand" aria-label="Expand">
        <i data-lucide="maximize-2" class="when-docked"></i><i data-lucide="minimize-2" class="when-full"></i>
      </button>
      <button type="button" class="laz-xv-btn" data-xv-close title="Close" aria-label="Close"><i data-lucide="x"></i></button>
    </div>
    <div class="laz-xv-fx"><span class="addr" data-xv-addr></span><i>fx</i><span class="val" data-xv-fx></span></div>
    <div class="laz-xv-body" data-xv-body></div>
    <div class="laz-xv-tabs" role="tablist" data-xv-tabs></div>`
  document.body.appendChild(el)
  startIcons(el)

  el.querySelector('[data-xv-close]').addEventListener('click', close)
  el.querySelector('[data-xv-expand]').addEventListener('click', () => {
    el.classList.toggle('is-full')
    grid?.resizeCanvas()
  })
  el.querySelector('[data-xv-scale]').addEventListener('change', (event) => {
    if (!scale) return
    scale.divisor = Number(event.target.value)
    grid?.invalidate()
    grid?.showFx()
  })
  el.querySelector('[data-xv-tabs]').addEventListener('click', (event) => {
    const tab = event.target.closest('[data-xv-tab]')
    if (tab) show(Number(tab.dataset.xvTab))
  })
  // Only while this panel is on the page: Turbo swaps the body on a visit, and the panel with it.
  const onKey = (event) => { if (event.key === 'Escape' && !el.hidden) close() }
  const onResize = () => grid?.resizeCanvas()
  document.addEventListener('keydown', onKey)
  window.addEventListener('resize', onResize)
  // Leaving the page: shut it first, so the copy Turbo keeps for Back has its sidebar and full width.
  document.addEventListener('turbo:before-cache', () => { if (!el.hidden) close() }, { once: true })
  document.addEventListener('turbo:before-render', () => {
    document.removeEventListener('keydown', onKey)
    window.removeEventListener('resize', onResize)
  }, { once: true })
  return el
}

function setScale(data) {
  const select = panel.querySelector('[data-xv-scale]')
  scale = data && {
    divisor: data.divisor, opening: data.divisor, options: data.options,
    money: new Intl.NumberFormat(data.indian ? 'en-IN' : 'en-US',
      { minimumFractionDigits: data.places, maximumFractionDigits: data.places, signDisplay: 'negative' })
  }
  select.hidden = !scale
  select.innerHTML = scale ? scale.options.map((o) => `<option value="${o.value}">${esc(o.name)}</option>`).join('') : ''
  if (scale) select.value = String(scale.divisor)
}

function close() {
  opened++
  setBody('')
  setScale(null)
  panel.hidden = true
  panel.classList.remove('is-full')
  window.dispatchEvent(new CustomEvent('pharos:sheet-close'))
}

function setBody(html) {
  grid?.destroy()
  grid = null
  setFx('', '')
  panel.querySelector('[data-xv-body]').innerHTML = html
}

function setFx(address, text) {
  panel.querySelector('[data-xv-addr]').textContent = address
  panel.querySelector('[data-xv-fx]').textContent = text
}

function show(index) {
  const sheet = sheets[index]
  panel.querySelectorAll('[data-xv-tab]').forEach((tab) => tab.setAttribute('aria-selected', String(tab.dataset.xvTab === String(index))))
  if (!sheet) return
  if (sheet.notes) {
    // Notes are sentences, and a grid row is one line high: drawn as text, so they wrap.
    const lines = everyRow(sheet.rows).map((row) => `<tr class="xv-${row.k}"><th>${row.r}</th><td>${esc(row.c[0])}</td></tr>`)
    setBody(`<div class="laz-xv-notes"><table><thead><tr><th></th><th>A</th></tr></thead><tbody>${lines.join('')}</tbody></table></div>`)
    return
  }
  setBody('<div class="laz-xv-grid"></div>')
  grid = drawGrid(panel.querySelector('.laz-xv-grid'), sheet)
}

function drawGrid(el, sheet) {
  const rows = everyRow(sheet.rows)
  const columns = [{
    id: 'rn', name: '', width: 44, resizable: false, selectable: false, focusable: false,
    cssClass: 'xv-rn', headerCssClass: 'xv-rn', formatter: (_r, _c, _v, _d, row) => (row.k === 'more' ? '' : row.r)
  }].concat(sheet.widths.map((width, i) => ({
    id: `c${i}`, name: letter(i), width: Math.round(width * CHAR_PX + 12),
    cssClass: sheet.n[i] ? 'xv-num' : '', formatter: (_r, _c, _v, _d, row) => esc(cellText(row, i))
  })))
  const data = {
    getLength: () => rows.length,
    getItem: (i) => rows[i],
    getItemMetadata: (i) => ({ cssClasses: `xv-${rows[i].k}`, columns: SPANS.has(rows[i].k) ? { 1: { colspan: '*' } } : undefined })
  }
  const g = new SlickGrid(el, data, columns, {
    rowHeight: ROW_HEIGHT, frozenColumn: 0, enableCellNavigation: true, enableColumnReorder: false,
    enableTextSelectionOnCells: true, explicitInitialization: true
  })
  g.getTextSelection = () => null // as data_rows_controller: keeps CodeMirror's selectionchange listener from crashing
  // The formula bar shows what the cell holds: its formula, else its value.
  g.showFx = () => {
    const at = g.getActiveCell()
    const row = at && rows[at.row]
    const i = at ? at.cell - 1 : -1
    if (!row || i < 0) return setFx('', '')
    setFx(`${letter(i)}${row.r}`, row.f?.[i] || cellText(row, i))
  }
  g.onActiveCellChanged.subscribe(() => g.showFx())
  g.init()
  return g
}
